import type { ReactNode } from "react";
import type { NotYetAvailableProps } from "@/components/NotYetAvailable";

// The typed definition a list page hands BaseListView (BRK-S211). A page
// "inherits" the list by declaring what it shows and what it can ask PMS for;
// BaseListView owns the rest: URL state, paging, layout and states.

/** A list control's two names: its key in the page's URL, and the query
 *  parameter it sends to PMS. Split so the URL stays short and readable while
 *  the API name is checked against the pinned contract (listViewDefsContract). */
export interface ListParam {
  urlKey: string;
  apiParam: string;
}

export type SortDir = "asc" | "desc";

export interface ListSortState {
  field: string;
  dir: SortDir;
}

/** What a cell or a header may read about the list's current view. */
export interface ListViewState {
  sort?: ListSortState;
}

export interface ListColumn<Row> {
  id: string;
  header: string | ((view: ListViewState) => string);
  cell: (row: Row, view: ListViewState) => ReactNode;
  /** `title` is the row's link and leads its card; `mono` sets record numbers
   *  in tabular figures. One title column per list. */
  kind?: "title" | "mono" | "text";
  /** Under a title: a second line (a DBA, a record number). */
  subtitle?: (row: Row) => ReactNode;
  align?: "left" | "right";
}

export interface ListSearch extends ListParam {
  label: string;
  placeholder?: string;
}

export interface ListToggle extends ListParam {
  label: string;
}

export interface ListFilterOption {
  value: string;
  label: string;
}

/** A select over values PMS accepts (BRK-S253). Its empty option, `allLabel`
 *  ("All statuses"), sends nothing, so PMS's own default applies. */
export interface ListFilter extends ListParam {
  label: string;
  allLabel: string;
  /** The values it offers: fixed, or read from PMS. */
  options: readonly ListFilterOption[] | ListFilterSource;
}

/** Options read from PMS when the list mounts (BRK-S254: the Product filter
 *  offers the active products). Until they arrive, a value in the URL waits,
 *  and the list with it, rather than being dropped or sent unchecked. If the
 *  read fails, the filter says so and the list loads without it. */
export interface ListFilterSource {
  /** The GET the options come from, checked against the pinned contract. */
  endpoint: `/api/broker/${string}`;
  load: () => Promise<readonly ListFilterOption[]>;
}

export function isOptionSource(
  options: ListFilter["options"],
): options is ListFilterSource {
  return "load" in options;
}

/** A parameter only a link sets (BRK-S253), e.g. one insured's accountId:
 *  read from the URL and sent to PMS, with no control. The list says it is
 *  narrowed (`note`), with a way back to everything. */
export interface ListLinkParam extends ListParam {
  note: string;
}

export interface ListSortOption {
  /** The API's sort field, e.g. `updatedAt`. */
  value: string;
  label: string;
  /** How each direction reads; dates default to newest / oldest first. */
  directions?: { desc: string; asc: string };
}

export interface ListSort {
  field: ListParam;
  dir: ListParam;
  options: readonly ListSortOption[];
  default: ListSortState;
}

/** What a row's actions may do to the list they sit in. */
export interface ListRowContext {
  reload: () => void;
}

/** What `load` is asked for, keyed by the definition's API parameters. */
export interface ListRequest {
  search?: string;
  /** Each declared toggle, by `apiParam`. */
  toggles: Record<string, boolean>;
  /** Each declared filter's value by `apiParam`, undefined for "all". */
  filters: Record<string, string | undefined>;
  /** Each declared link param's value by `apiParam`, undefined when unset. */
  links: Record<string, string | undefined>;
  sort?: ListSortState;
  limit: number;
  offset: number;
}

/** PMS's list envelope. */
export interface Page<Row> {
  items: Row[];
  total: number;
  limit: number;
  offset: number;
  /** A footer under the rows (BRK-S406). */
  totals?: ListTotals;
}

/**
 * A totals row: what EVERY row the request matched adds up to, not only this
 * page's — so only a loader that holds the whole set (or a PMS that serves the
 * aggregates) may fill it. Keyed by column id: the title column's entry labels
 * the row ("7 submissions"), and a column with no entry is blank.
 */
export type ListTotals = Partial<Record<string, ReactNode>>;

export interface BaseListViewDef<Row> {
  /** The GET it lists, exactly as pinned in the PMS contract. */
  endpoint: `/api/broker/${string}`;
  /** Plural noun for copy: "Loading accounts…", "No accounts match…". */
  noun: string;
  title: string;
  context?: ReactNode;
  /** The header's call to action (e.g. "Add account"). */
  primaryAction?: ReactNode;
  columns: readonly ListColumn<Row>[];
  rowKey: (row: Row) => string;
  /** Where the row goes, or null for a row with nowhere to go, whose title
   *  then renders as text (BRK-S252: a notice about no record). */
  rowHref: (row: Row) => string | null;
  /** The row link's accessible name; it should start with the title text. */
  rowLabel: (row: Row) => string;
  /** The row's own controls (BRK-S252), in a trailing cell or under the card.
   *  `reload` re-reads the current page, for an action that changes it. */
  rowActions?: (row: Row, list: ListRowContext) => ReactNode;
  search?: ListSearch;
  toggles?: readonly ListToggle[];
  filters?: readonly ListFilter[];
  linkParams?: readonly ListLinkParam[];
  sort?: ListSort;
  /** Rows per page, at most PMS's 100. */
  pageSize: number;
  /** The URL key the page number lives in, `page` by default. An embedded
   *  list sharing its page with another names its own (BRK-S406), as its
   *  filters' `urlKey`s do, so paging one never pages the other. */
  pageKey?: string;
  /** Names what the pager counts — "Showing 1–10 of 46 documents" — where the
   *  wireframe does (BRK-S440); left out, the line ends at the total. */
  totalNoun?: string;
  /** The first-run empty state; a filtered-empty list has its own copy. */
  empty: { title: string; body?: ReactNode; action?: ReactNode };
  /** A wireframe table wider than six columns (BRK-S501): up to nine, shown
   *  as cards below lg and as a table that scrolls inside its own frame from
   *  lg, so the page never scrolls sideways. */
  wide?: boolean;
  load: (request: ListRequest) => Promise<Page<Row>>;
}

/** A list PMS can't serve yet (BRK-S251). BaseListView shows its header and the
 *  columns it will have over a not-yet-available panel that says where the
 *  data lives today, and asks PMS for nothing: no rows, no controls. The story
 *  that wires it replaces it with a `defineListView` definition. */
export interface PendingListViewDef {
  /** The GET planned to serve it, recorded for the story that wires it. */
  plannedEndpoint: `/api/broker/${string}`;
  noun: string;
  title: string;
  context?: ReactNode;
  /** The planned column headers, so the page shows the list's shape. */
  columns: readonly string[];
  notYetAvailable: NotYetAvailableProps;
}

/** Either kind of definition a list page can hand BaseListView. */
export type AnyListViewDef<Row> = BaseListViewDef<Row> | PendingListViewDef;

export function isPendingListView<Row>(
  def: AnyListViewDef<Row>,
): def is PendingListViewDef {
  return "notYetAvailable" in def;
}
